import { Check } from "lucide-react";
import { Eyebrow, Reveal } from "@/components/Reveal";

const TIERS = [
    {
        id: "a",
        letter: "A",
        name: "Mega & Macro",
        reach: "1M+",
        unit: "followers / organic reach",
        role: "Mass awareness & cultural authority",
        bestFor: "Product launches & national brand signals",
        perks: ["High production value", "Household names", "Global reach"],
        accent: "from-crimson to-flame",
    },
    {
        id: "b",
        letter: "B",
        name: "Mid-Tier Specialists",
        reach: "100K+",
        unit: "followers",
        role: "High engagement & niche expertise",
        bestFor: "Category domination & performance UGC",
        perks: ["Strong loyalty", "High CTRs", "Rapid turnaround"],
        accent: "from-flame to-amber-400",
    },
    {
        id: "c",
        letter: "C",
        name: "Micro & Native",
        reach: "10K+",
        unit: "followers",
        role: "Hyper-local trust & authenticity",
        bestFor: "Regional penetration & conversion whitelisting",
        perks: ["Peak conversion rates", "Cost efficiency", "Regional nuance"],
        accent: "from-blue-500 to-cyan-400",
    },
];

export default function Tiers() {
    return (
        <section id="tiers" data-testid="tiers-section" className="border-y border-line bg-white py-16 sm:py-24">
            <div className="mx-auto max-w-6xl px-5 sm:px-8">
                <Reveal className="mb-12 max-w-3xl">
                    <Eyebrow tone="light">Creator network</Eyebrow>
                    <h2 className="mt-4 font-syne text-3xl font-bold tracking-tight text-ink sm:text-5xl">
                        The right tier for
                        <br />
                        the right job.
                    </h2>
                    <p className="mt-5 text-base leading-relaxed text-slate-600 sm:text-lg">
                        Every campaign objective maps to a creator tier. We match, brief and
                        track across all three.
                    </p>
                </Reveal>

                <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
                    {TIERS.map((t, i) => (
                        <Reveal key={t.id} delay={i * 0.1} className="h-full" testid={`tier-card-${t.id}`}>
                            <div className="group relative flex h-full flex-col overflow-hidden rounded-3xl border border-line bg-paper p-8 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-2xl hover:shadow-slate-200">
                                <div
                                    className={`absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r ${t.accent}`}
                                    aria-hidden="true"
                                />
                                <span
                                    className="pointer-events-none absolute -right-3 -top-6 select-none font-syne text-[7rem] font-extrabold leading-none text-ink/[0.05] transition-colors duration-300 group-hover:text-crimson/10"
                                    aria-hidden="true"
                                >
                                    {t.letter}
                                </span>
                                <p className="font-mono text-[11px] uppercase tracking-[0.24em] text-slate-500">
                                    Tier {t.letter} — {t.name}
                                </p>
                                <p className="mt-6 font-syne text-5xl font-extrabold tracking-tight text-ink">
                                    {t.reach}
                                </p>
                                <p className="mt-1 text-xs text-slate-500">{t.unit}</p>
                                <p className="mt-6 text-sm font-semibold text-ink">{t.role}</p>
                                <p className="mt-2 text-sm text-slate-600">Best for: {t.bestFor}</p>
                                <ul className="mt-6 space-y-2.5 border-t border-line pt-6">
                                    {t.perks.map((p) => (
                                        <li key={p} className="flex items-center gap-2.5 text-sm text-slate-700">
                                            <Check className="h-4 w-4 text-crimson" />
                                            {p}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>
    );
}
